import {
  ANALYTICS_EXPORT_REPORT_TITLE,
  formatThroughputLineNames,
  getExportChartTitle,
  getPopulationScopeSummaryLines,
  getUnavailableChartMessage,
  scaleToFit,
} from "@/features/deployment-dashboard/analytics/export/analytics-export.utils";
import type {
  AnalyticsExportChartSnapshot,
  AnalyticsExportReportMetadata,
  AnalyticsExportTheme,
  ExportChartAvailability,
  ExportChartId,
} from "@/features/deployment-dashboard/analytics/export/types";
import { EXPORT_CHART_OPTIONS } from "@/features/deployment-dashboard/analytics/export/types";

const PDF_PAGE = {
  width: 297,
  height: 210,
  outerMargin: 14,
  /** Preserves the pre-existing panel offset for exports without a scope note. */
  headerHeight: 38,
  panelPadding: 6,
} as const;

const TITLE_FONT_SIZE = 18;
const HEADER_LINE_FONT_SIZE = 10;
const CHART_TITLE_FONT_SIZE = 14;
const UNAVAILABLE_FONT_SIZE = 12;
const HEADER_LINE_HEIGHT = 5;

export async function createAnalyticsPdf(
  charts: readonly AnalyticsExportChartSnapshot[],
  selectedCharts: readonly ExportChartId[],
  metadata: AnalyticsExportReportMetadata,
  theme: AnalyticsExportTheme
): Promise<Blob> {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  doc.setProperties({
    title: ANALYTICS_EXPORT_REPORT_TITLE,
    creator: "Situate",
  });
  doc.setCreationDate(new Date(metadata.exportedAtIso));

  const selectedChartIds = new Set(selectedCharts);
  const orderedCharts = EXPORT_CHART_OPTIONS.filter((option) => selectedChartIds.has(option.value));

  for (const [index, option] of orderedCharts.entries()) {
    if (index > 0) {
      doc.addPage();
    }

    const chart = charts.find((candidate) => candidate.id === option.value);
    await drawPdfPage(doc, option.value, chart, metadata, theme);
  }

  return doc.output("blob");
}

async function drawPdfPage(
  doc: import("jspdf").jsPDF,
  chartId: ExportChartId,
  chart: AnalyticsExportChartSnapshot | undefined,
  metadata: AnalyticsExportReportMetadata,
  theme: AnalyticsExportTheme
): Promise<void> {
  doc.setFillColor(theme.reportBackground);
  doc.rect(0, 0, PDF_PAGE.width, PDF_PAGE.height, "F");

  const headerHeight = drawHeader(doc, chartId, chart, metadata, theme);

  const panelLeft = PDF_PAGE.outerMargin;
  const panelTop = PDF_PAGE.outerMargin + headerHeight;
  const panelWidth = PDF_PAGE.width - PDF_PAGE.outerMargin * 2;
  const panelHeight = PDF_PAGE.height - panelTop - PDF_PAGE.outerMargin;

  doc.setFillColor(theme.surfaceBackground);
  doc.rect(panelLeft, panelTop, panelWidth, panelHeight, "F");

  if (chart?.availability === "ready" && chart.image != null) {
    await drawChartImage(doc, chart.image, {
      left: panelLeft + PDF_PAGE.panelPadding,
      top: panelTop + PDF_PAGE.panelPadding,
      width: panelWidth - PDF_PAGE.panelPadding * 2,
      height: panelHeight - PDF_PAGE.panelPadding * 2,
    });
  } else {
    drawUnavailableMessage(
      doc,
      chart?.availability ?? "not-rendered",
      {
        left: panelLeft + PDF_PAGE.panelPadding,
        top: panelTop + PDF_PAGE.panelPadding,
        width: panelWidth - PDF_PAGE.panelPadding * 2,
        height: panelHeight - PDF_PAGE.panelPadding * 2,
      },
      theme
    );
  }
}

/** Draws the page header and returns the total header height used, so the panel below can start after it. */
function drawHeader(
  doc: import("jspdf").jsPDF,
  chartId: ExportChartId,
  chart: AnalyticsExportChartSnapshot | undefined,
  metadata: AnalyticsExportReportMetadata,
  theme: AnalyticsExportTheme
): number {
  const left = PDF_PAGE.outerMargin;
  let top = PDF_PAGE.outerMargin;

  doc.setTextColor(theme.primaryText);
  doc.setFontSize(TITLE_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  top += 6;
  doc.text(ANALYTICS_EXPORT_REPORT_TITLE, left, top);

  doc.setFontSize(CHART_TITLE_FONT_SIZE);
  top += 7;
  doc.text(getExportChartTitle(chartId), left, top);

  doc.setTextColor(theme.secondaryText);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(HEADER_LINE_FONT_SIZE);
  top += HEADER_LINE_HEIGHT;
  doc.text(`Date range: ${metadata.dateRangeLabel}`, left, top);
  top += HEADER_LINE_HEIGHT;
  doc.text(`Area: ${metadata.areaLabel}`, left, top);

  if (
    chartId === "throughput-trend" &&
    metadata.throughputLines != null &&
    metadata.throughputLines.length > 0
  ) {
    top += HEADER_LINE_HEIGHT;
    doc.text(
      `Lines: ${formatThroughputLineNames(metadata.throughputLines).join(", ")}`,
      left,
      top,
      { maxWidth: PDF_PAGE.width - PDF_PAGE.outerMargin * 2 }
    );
  }

  let scopeLineCount = 0;
  if (
    chartId === "population-trend" &&
    chart?.id === "population-trend" &&
    chart.availability === "ready"
  ) {
    const scopeLines = getPopulationScopeSummaryLines(chart.populationScope);
    if (scopeLines != null) {
      scopeLineCount = scopeLines.length;
      for (const line of scopeLines) {
        top += HEADER_LINE_HEIGHT;
        doc.text(line, left, top, { maxWidth: PDF_PAGE.width - PDF_PAGE.outerMargin * 2 });
      }
    }
  }

  top += HEADER_LINE_HEIGHT;
  doc.text(`Exported: ${metadata.exportedAtLabel}`, left, top);

  return PDF_PAGE.headerHeight + scopeLineCount * HEADER_LINE_HEIGHT;
}

async function drawChartImage(
  doc: import("jspdf").jsPDF,
  image: Blob,
  panel: { left: number; top: number; width: number; height: number }
): Promise<void> {
  const bytes = new Uint8Array(await image.arrayBuffer());
  const properties = doc.getImageProperties(bytes);
  const scale = scaleToFit(properties.width, properties.height, panel.width, panel.height);

  if (scale.width <= 0 || scale.height <= 0) {
    return;
  }

  const left = panel.left + (panel.width - scale.width) / 2;
  const top = panel.top + (panel.height - scale.height) / 2;

  doc.addImage(bytes, "PNG", left, top, scale.width, scale.height);
}

function drawUnavailableMessage(
  doc: import("jspdf").jsPDF,
  availability: ExportChartAvailability,
  panel: { left: number; top: number; width: number; height: number },
  theme: AnalyticsExportTheme
): void {
  const message =
    getUnavailableChartMessage(availability) ?? "No rendered chart image was available.";
  doc.setTextColor(theme.secondaryText);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(UNAVAILABLE_FONT_SIZE);
  doc.text(message, panel.left, panel.top + 8, { maxWidth: panel.width });
}
